import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { View } from '../types';
import { Mail, Lock, User, Phone, MapPin, ArrowRight, Loader2, Eye, ShieldCheck, CheckCircle, AlertTriangle, Home, Globe, Map, X, Stethoscope, Zap, Sparkles, Building2, Search, Send, Key, ChevronDown } from 'lucide-react';
import { ForcePasswordChange } from './ForcePasswordChange';
import { signInWithGoogle } from '../services/firebase';
import { LOCATIONS } from '../data/locations'; // Importado de la fuente centralizada
import { formatGenericID } from '../utils/formatters';

// Dropdown personalizado que evita el picker nativo de Android (que se muestra negro)
const CustomSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
}> = ({ value, onChange, options, placeholder, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className="w-full flex items-center justify-between outline-none bg-transparent font-bold text-xs disabled:text-slate-300 cursor-pointer text-left"
      >
        <span className={value ? 'text-slate-700' : 'text-slate-400'}>{value || placeholder}</span>
        <ChevronDown size={14} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 z-[9999] mt-2 bg-white border-2 border-[#FF6B00]/30 rounded-2xl shadow-2xl max-h-52 overflow-y-auto">
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-xs font-bold hover:bg-orange-50 transition-colors first:rounded-t-2xl last:rounded-b-2xl ${value === opt ? 'text-[#FF6B00] bg-orange-50' : 'text-slate-700'}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface AuthViewProps {
  onNavigate: (view: View) => void;
  onSuccess?: (user?: any) => void;
  initialMode?: 'SELECTION' | 'CLASSIC' | 'NEW_ENV' | 'VET' | 'BREEDER' | 'SHELTER';
  hideRegister?: boolean;
  role?: 'user' | 'vet' | 'breeder' | 'shelter';
  initialEmail?: string;
}

// En una APK nativa (Usuario/Veterinaria/Albergue/Criadero) el entorno de
// destino lo fija el propio build (VITE_APP_TARGET), no los roles de la
// cuenta — antes, una cuenta con más de un rol (ej. dueño de mascota Y
// criador) siempre caía en el panel de mayor privilegio sin importar en qué
// APK inició sesión, aunque esa APK solo tenga sentido para un entorno.
const nativePortalTarget = (): View | null => {
  if (!Capacitor.isNativePlatform()) return null;
  const target = (import.meta as any).env?.VITE_APP_TARGET || 'USER';
  if (target === 'VET') return View.VET_DASHBOARD;
  if (target === 'BREEDER') return View.BREEDER_DASHBOARD;
  if (target === 'SHELTER') return View.SHELTER_DASHBOARD;
  return View.USER_HUB;
};

export const LoginView: React.FC<AuthViewProps> = ({ onNavigate, onSuccess, initialMode = 'CLASSIC', hideRegister = false, initialEmail = '' }) => {
  const { login } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modales Recuperación
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showSentModal, setShowSentModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // State for Password Change Force
  const [showForcePassword, setShowForcePassword] = useState(false);
  const [tempUser, setTempUser] = useState<any>(null);

  // State for Login Flow
  const [loginMode, setLoginMode] = useState<'SELECTION' | 'CLASSIC' | 'NEW_ENV' | 'VET' | 'BREEDER' | 'SHELTER'>(initialMode);

  useEffect(() => {
    if (initialMode) {
      setLoginMode(initialMode);
    }
  }, [initialMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await api.login(email, password);

      if (res && res.message === 'EXITO' && res.user) {
        login(res.user, res.token);

        if (onSuccess) {
          onSuccess(res.user);
          return;
        }

        if (localStorage.getItem('checkout_pending')) {
          localStorage.removeItem('checkout_pending');
          onNavigate(View.CHECKOUT);
          return;
        }

        // El modal "paso a paso" de dar en adopción es solo para cuentas recién
        // creadas (sin mascotas aún) — lo dispara RegisterView. Si quien inicia
        // sesión acá ya tenía cuenta, probablemente ya tiene mascotas cargadas:
        // sigue el login normal y solo se limpia la bandera para que no quede
        // pegada de una visita anterior a Adóptame.
        localStorage.removeItem('adoption_give_pending');

        // Check if password change is forced (Disabled by user request)
        /*
        if (res.user.must_change_password) {
          setTempUser(res.user);
          setShowForcePassword(true);
          return;
        }
        */

        // NAVIGATE BASED ON LOGIN MODE
        if (loginMode === 'NEW_ENV') {
          // Force URL update as backup
          const url = new URL(window.location.href);
          url.searchParams.set('view', 'PET_ENVIRONMENT');
          window.history.pushState({}, '', url);

          onNavigate(View.PET_ENVIRONMENT);
        } else if (res.user.role?.includes('root')) {
          onNavigate(View.ROOT_PANEL);
        } else if (nativePortalTarget()) {
          onNavigate(nativePortalTarget()!);
        } else if (loginMode === 'VET' || res.user.role?.includes('vet')) {
          onNavigate(View.VET_DASHBOARD);
        } else if (loginMode === 'BREEDER' || res.user.role?.includes('breeder')) {
          onNavigate(View.BREEDER_DASHBOARD);
        } else if (loginMode === 'SHELTER' || res.user.role?.includes('shelter')) {
          onNavigate(View.SHELTER_DASHBOARD);
        } else {
          onNavigate(View.USER_HUB);
        }
      } else {
        setError(res.error || 'Credenciales inválidas');
      }
    } catch (e) {
      setError(t.auth.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    const fakeUser = {
      id: '999',
      name: 'Sophia Herrera',
      email: 'pruskaminu@gmail.com',
      phone: '+56 9 2035 3270',
      rut: '23.878.205-8',
      region: 'Coquimbo',
      city: 'La Serena',
      address: 'Siena 361, Depto 402',
      imageUrl: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&q=80&w=200'
    };
    login(fakeUser);
    onNavigate(View.USER_HUB);
    setDemoLoading(false);
  };

  const handleVetDemoLogin = async () => {
    setDemoLoading(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    const fakeVet = {
      id: 'vet-1',
      name: 'Dr. Ricardo Pérez',
      email: 'vet@amigopeludo.cl',
      role: 'vet',
      imageUrl: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&q=80&w=200'
    };
    login(fakeVet as any);
    onNavigate(View.VET_DASHBOARD);
    setDemoLoading(false);
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setError('');
    const res = await signInWithGoogle();
    if (res.success && res.user) {
      try {
        const apiRes = await api.socialLogin(res.user.email || '', res.user.name || 'Usuario', 'google');
        if (apiRes && apiRes.message === 'EXITO' && apiRes.user) {
          login(apiRes.user, apiRes.token);
          if (onSuccess) {
            onSuccess(apiRes.user);
            return;
          }
          if (localStorage.getItem('checkout_pending')) {
            localStorage.removeItem('checkout_pending');
            onNavigate(View.CHECKOUT);
            return;
          }
          // Igual que en el login con contraseña: el modal de dar en adopción es
          // solo para registros nuevos (lo dispara RegisterView). Acá solo se
          // limpia la bandera por si quedó pegada de una visita a Adóptame.
          localStorage.removeItem('adoption_give_pending');
          const fixedTarget = nativePortalTarget();
          if (apiRes.user.role?.includes('root')) onNavigate(View.ROOT_PANEL);
          else if (fixedTarget) onNavigate(fixedTarget);
          else if (apiRes.user.role?.includes('vet')) onNavigate(View.VET_DASHBOARD);
          else if (apiRes.user.role?.includes('breeder')) onNavigate(View.BREEDER_DASHBOARD);
          else if (apiRes.user.role?.includes('shelter')) onNavigate(View.SHELTER_DASHBOARD);
          else onNavigate(View.USER_HUB);
        } else {
          setError(apiRes.error || 'Error al iniciar sesión con Google');
        }
      } catch (e) {
        setError(t.auth.connectionError);
      }
    } else {
      setError(res.error || 'Autenticación con Google cancelada o fallida');
    }
    setLoading(false);
  };

  if (showForcePassword && tempUser) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[85vh] px-4 animate-in fade-in slide-in-from-bottom-4 py-12 pb-32">
        <ForcePasswordChange
          user={tempUser}
          onSuccess={() => {
            setShowForcePassword(false);
            // After changing password, continue with normal flow
            const role = tempUser.role;
            if (role?.includes('root')) onNavigate(View.ROOT_PANEL);
            else if (role?.includes('vet')) onNavigate(View.VET_DASHBOARD);
            else if (role?.includes('breeder')) onNavigate(View.BREEDER_DASHBOARD);
            else if (role?.includes('shelter')) onNavigate(View.SHELTER_DASHBOARD);
            else onNavigate(View.USER_HUB);
          }}
        />
      </div>
    );
  }

  // ─── APK Usuario: pantalla completa celeste sin contenedor ─────────────────
  // OJO: esta pantalla es "hideUI" así que <main> ya no le reserva nada de
  // área segura (ver App.tsx) — el pt de acá es la única reserva, y los
  // tamaños se ajustaron para que las 3 secciones (logo, formulario, Google)
  // quepan siempre en una sola pantalla sin scroll.
  if (Capacitor.isNativePlatform() && loginMode === 'CLASSIC') {
    return (
      <div className="flex flex-col h-[100dvh] bg-gradient-to-b from-[#BAE6FD] to-[#38BDF8] px-6 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-4 justify-center gap-6 overflow-y-auto">

        {/* Logo / cabecera */}
        <div className="flex flex-col items-center shrink-0">
          <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-lg mb-2">
            <img src="/icon.png" alt="Amigo Peludo" className="w-full h-full object-cover" />
          </div>
          <h1 className="text-2xl font-black text-sky-900 italic uppercase tracking-tighter">Bienvenido</h1>
          <p className="text-sky-800/70 text-[9px] font-black uppercase tracking-[0.3em] mt-0.5">Ingresa tus credenciales</p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-2.5 shrink-0">
          {error && (
            <div className="bg-red-100/80 border border-red-300/50 p-2.5 rounded-xl flex items-center gap-2 text-red-700 text-xs font-bold">
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          <div>
            <p className="text-sky-900 text-[9px] font-black uppercase tracking-widest mb-1">Correo</p>
            <div className="flex items-center bg-white/40 border border-white/60 rounded-xl px-4 py-2.5 gap-3">
              <Mail size={16} className="text-sky-700 shrink-0" />
              <input
                type="email" autoComplete="email"
                placeholder="tu@email.com"
                value={email} onChange={e => setEmail(e.target.value)}
                className="bg-transparent text-sky-900 placeholder-sky-700/50 text-sm font-bold flex-1 outline-none"
              />
            </div>
          </div>

          <div>
            <p className="text-sky-900 text-[9px] font-black uppercase tracking-widest mb-1">Contraseña</p>
            <div className="flex items-center bg-white/40 border border-white/60 rounded-xl px-4 py-2.5 gap-3">
              <Lock size={16} className="text-sky-700 shrink-0" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••"
                value={password} onChange={e => setPassword(e.target.value)}
                className="bg-transparent text-sky-900 placeholder-sky-700/50 text-sm font-bold flex-1 outline-none"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-sky-700">
                <Eye size={16} />
              </button>
            </div>
          </div>

          <button
            type="submit" disabled={loading}
            className="w-full bg-[#0369A1] text-white py-3 rounded-xl font-black uppercase text-sm shadow-lg shadow-sky-900/20 transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <>Ingresar <ArrowRight size={16} /></>}
          </button>

          <div className="text-center">
            <button type="button"
              onClick={() => { setForgotEmail(email); setForgotError(''); setShowForgotModal(true); }}
              className="text-sky-800/70 text-[9px] font-black uppercase tracking-widest"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <div className="text-center bg-white/30 rounded-xl py-2 px-4 border border-white/50">
            <p className="text-sky-900 text-xs font-black">¡No tienes cuenta!</p>
            <button type="button"
              onClick={() => onNavigate(View.REGISTER)}
              className="text-[#0369A1] font-black uppercase text-[11px] mt-0.5 underline underline-offset-2 tracking-widest"
            >
              Crear cuenta gratis →
            </button>
          </div>
        </form>

        {/* Google */}
        <div className="flex flex-col gap-1.5 shrink-0">
          <p className="text-sky-800/60 text-[9px] font-black uppercase tracking-widest text-center">O ingresa con</p>
          <button type="button"
            className="w-full flex items-center justify-center gap-3 bg-white/40 border border-white/60 py-2.5 rounded-xl font-bold text-sm text-sky-900 transition-all active:scale-95"
            onClick={handleGoogleAuth}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Google
          </button>
        </div>

        {/* Modal recuperar contraseña */}
        {showForgotModal && (
          <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-6">
            <div className="bg-white rounded-[2.5rem] p-8 max-w-sm w-full shadow-2xl relative">
              <button onClick={() => setShowForgotModal(false)} className="absolute top-6 right-6 text-slate-300 hover:text-rose-500">
                <X size={20} />
              </button>
              <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mb-6">
                <Lock size={28} className="text-sky-600" />
              </div>
              <h3 className="text-xl font-black text-slate-800 uppercase italic tracking-tighter mb-2">Recuperar Clave</h3>
              <p className="text-slate-500 font-bold text-xs mb-6 leading-relaxed">Ingresa tu correo y te enviaremos un enlace para recuperar tu contraseña.</p>
              {forgotError && <div className="bg-rose-50 text-rose-500 p-3 rounded-xl text-[10px] font-bold mb-4">{forgotError}</div>}
              <div className="space-y-4">
                <div className="flex items-center bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3.5 gap-3">
                  <Mail size={16} className="text-slate-400" />
                  <input type="email" placeholder="email@ejemplo.com" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} className="bg-transparent text-slate-800 text-sm font-bold flex-1 outline-none" />
                </div>
                <button disabled={forgotLoading}
                  onClick={async () => {
                    if (!forgotEmail) { setForgotError("Ingresa tu correo"); return; }
                    setForgotLoading(true); setForgotError('');
                    try {
                      const res = await api.forgotPassword(forgotEmail);
                      if (res.success) { setShowForgotModal(false); setShowSentModal(true); }
                      else setForgotError(res.message || "Error al procesar");
                    } catch { setForgotError("Error de conexión"); }
                    finally { setForgotLoading(false); }
                  }}
                  className="w-full bg-sky-600 text-white py-4 rounded-2xl font-black uppercase text-xs shadow-lg flex items-center justify-center gap-2"
                >
                  {forgotLoading ? <Loader2 size={16} className="animate-spin" /> : <>Enviar Enlace <ArrowRight size={16} /></>}
                </button>
              </div>
            </div>
          </div>
        )}

        {showSentModal && (
          <div className="fixed inset-0 z-[101] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-6">
            <div className="bg-white rounded-[2.5rem] p-10 max-w-sm w-full text-center shadow-2xl">
              <div className="w-20 h-20 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-white" />
              </div>
              <h3 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter mb-2">¡Correo Enviado!</h3>
              <p className="text-slate-500 font-bold text-xs mb-8 leading-relaxed">Hemos enviado las instrucciones a <span className="text-slate-800">{forgotEmail}</span>. Revisa tu bandeja de entrada.</p>
              <button onClick={() => setShowSentModal(false)} className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black uppercase text-xs">Entendido</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] px-4 animate-in fade-in slide-in-from-bottom-4">
      <div className={`bg-white p-8 rounded-[3rem] shadow-xl border-4 ${loginMode === 'VET' ? 'border-[#1E3A8A]/20' : loginMode === 'BREEDER' ? 'border-slate-800/20' : 'border-[#FF6B00]/20'} w-full max-w-md relative overflow-hidden`}>
        <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${loginMode === 'VET' ? 'from-[#1E3A8A] to-blue-400' : loginMode === 'BREEDER' ? 'from-slate-800 to-slate-600' : 'from-[#FF6B00] to-orange-400'}`}></div>

        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {loginMode === 'SELECTION' && <Sparkles size={40} className="text-[#FF6B00] animate-pulse" />}
            {loginMode === 'CLASSIC' && <User size={40} className="text-[#FF6B00]" />}
            {loginMode === 'NEW_ENV' && <Zap size={40} className="text-[#FF6B00]" />}
            {loginMode === 'VET' && <Stethoscope size={40} className="text-[#1E3A8A]" />}
            {loginMode === 'BREEDER' && <ShieldCheck size={40} className="text-slate-800" />}
          </div>
          <h2 className="text-3xl font-black text-slate-800 italic uppercase tracking-tighter">
            {loginMode === 'SELECTION' ? 'Elige tu Perfil' :
              loginMode === 'VET' ? 'Acceso Veterinario' :
                loginMode === 'BREEDER' ? 'Acceso Criadores' :
                  loginMode === 'NEW_ENV' ? 'Entorno Ringo' : 'Bienvenido'}
          </h2>
          <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-2">
            {loginMode === 'SELECTION' ? '¿Cómo deseas ingresar hoy?' : 'Ingresa tus credenciales'}
          </p>
        </div>

        {loginMode === 'SELECTION' ? (
          <div className="grid grid-cols-1 gap-4">
            {/* OPCIÓN 1: USUARIO CLÁSICO */}
            <button
              onClick={() => setLoginMode('CLASSIC')}
              className="group relative overflow-hidden rounded-[2rem] p-1 transition-all hover:scale-[1.02] text-left"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-[#1E3A8A] opacity-10 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-white p-6 rounded-[1.9rem] border-2 border-slate-100 group-hover:border-transparent flex items-center gap-4 transition-all">
                <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A]/10 text-[#1E3A8A] flex items-center justify-center group-hover:bg-white group-hover:text-[#1E3A8A] transition-colors shrink-0">
                  <User size={24} strokeWidth={2.5} />
                </div>
                <div className="text-left w-full">
                  <h4 className="font-black text-slate-800 text-sm uppercase italic group-hover:text-white transition-colors">Usuario Clásico</h4>
                  <p className="text-[10px] text-slate-400 font-bold group-hover:text-blue-50 transition-colors">Dashboard Original v2.0</p>
                </div>
              </div>
            </button>

            {/* OPCIÓN 2: NUEVO ENTORNO RICH (NEXUS SENTINEL) */}
            <button
              onClick={() => setLoginMode('NEW_ENV')}
              className="group relative overflow-hidden rounded-[2rem] p-1 transition-all hover:scale-[1.02] text-left"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B00] to-[#EF4444] opacity-10 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-white p-6 rounded-[1.9rem] border-2 border-orange-50 group-hover:border-transparent flex items-center gap-4 transition-all">
                <div className="w-12 h-12 rounded-2xl bg-orange-100 text-[#FF6B00] flex items-center justify-center group-hover:bg-white group-hover:text-[#FF6B00] transition-colors shrink-0">
                  <Sparkles size={24} strokeWidth={2.5} />
                </div>
                <div className="text-left w-full">
                  <h4 className="font-black text-slate-800 text-sm uppercase italic group-hover:text-white transition-colors">Entorno Nexus (v2.0)</h4>
                  <p className="text-[10px] text-slate-400 font-bold group-hover:text-orange-100 transition-colors">Diseño Inmersivo Sentinel</p>
                </div>
                <div className="absolute top-4 right-4 bg-orange-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest animate-pulse">PRO</div>
              </div>
            </button>

            {/* OPCIÓN 3: VETERINARIA */}
            <button
              onClick={() => setLoginMode('VET')}
              className="group relative overflow-hidden rounded-[2rem] p-1 transition-all hover:scale-[1.02] text-left"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A8A] to-blue-400 opacity-10 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-white p-6 rounded-[1.9rem] border-2 border-blue-50 group-hover:border-transparent flex items-center gap-4 transition-all">
                <div className="w-12 h-12 rounded-2xl bg-blue-100 text-[#1E3A8A] flex items-center justify-center group-hover:bg-white group-hover:text-[#1E3A8A] transition-colors shrink-0">
                  <Stethoscope size={24} strokeWidth={2.5} />
                </div>
                <div className="text-left w-full">
                  <h4 className="font-black text-slate-800 text-sm uppercase italic group-hover:text-white transition-colors">Portal Veterinario</h4>
                  <p className="text-[10px] text-slate-400 font-bold group-hover:text-blue-50 transition-colors">Gestión Clínica Nexus</p>
                </div>
              </div>
            </button>

            {/* OPCIÓN 4: CRIADERO PROFESIONAL */}
            <button
              onClick={() => setLoginMode('BREEDER')}
              className="group relative overflow-hidden rounded-[2rem] p-1 transition-all hover:scale-[1.02] text-left"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-slate-700 to-slate-900 opacity-10 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-white p-6 rounded-[1.9rem] border-2 border-slate-100 group-hover:border-transparent flex items-center gap-4 transition-all">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-800 flex items-center justify-center group-hover:bg-white group-hover:text-slate-800 transition-colors shrink-0">
                  <ShieldCheck size={24} strokeWidth={2.5} />
                </div>
                <div className="text-left w-full">
                  <h4 className="font-black text-slate-800 text-sm uppercase italic group-hover:text-white transition-colors">Soy Criadero</h4>
                  <p className="text-[10px] text-slate-400 font-bold group-hover:text-slate-200 transition-colors">Gestión de Camadas y QR</p>
                </div>
              </div>
            </button>

            {/* OPCIÓN 5: ALBERGUE / RESCATE */}
            <button
              onClick={() => setLoginMode('SHELTER')}
              className="group relative overflow-hidden rounded-[2rem] p-1 transition-all hover:scale-[1.02] text-left"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-700 opacity-10 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-white p-6 rounded-[1.9rem] border-2 border-emerald-50 group-hover:border-transparent flex items-center gap-4 transition-all">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center group-hover:bg-white group-hover:text-emerald-600 transition-colors shrink-0">
                  <Home size={24} strokeWidth={2.5} />
                </div>
                <div className="text-left w-full">
                  <h4 className="font-black text-slate-800 text-sm uppercase italic group-hover:text-white transition-colors">Albergue / Rescate</h4>
                  <p className="text-[10px] text-slate-400 font-bold group-hover:text-emerald-100 transition-colors">Gestión de Adopciones</p>
                </div>
              </div>
            </button>
          </div>
        ) : (
          <>
            {!Capacitor.isNativePlatform() && initialMode === 'SELECTION' && (
              <button onClick={() => setLoginMode('SELECTION')} className="absolute top-8 left-8 text-slate-400 hover:text-slate-600 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest transition-colors">
                <ArrowRight size={12} className="rotate-180" /> Volver
              </button>
            )}

            {error && (
              <div className="bg-rose-50 border border-rose-100 p-4 rounded-2xl mb-6 flex items-center gap-3 text-rose-500 text-xs font-bold animate-in fade-in zoom-in-95">
                <AlertTriangle size={16} /> {error}
              </div>
            )}

            {success && (
              <div className="bg-emerald-50 border border-[#FF6B00]/20 p-4 rounded-2xl mb-6 flex items-center gap-3 text-[#FF6B00] text-xs font-bold animate-in fade-in zoom-in-95">
                <CheckCircle size={16} /> {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <InputGroup icon={Mail} label={t.auth.email} type="email" placeholder="tu@email.com" value={email} onChange={(e: any) => setEmail(e.target.value)} />

              <InputGroup
                icon={Lock}
                label={t.auth.password}
                type={showPassword ? "text" : "password"}
                placeholder="••••••"
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                isPassword={true}
                onTogglePassword={() => setShowPassword(!showPassword)}
                showPassword={showPassword}
              />

              <button
                type="submit"
                disabled={loading || demoLoading}
                className="w-full bg-[#FF6B00] hover:bg-[#e66000] text-white py-4 rounded-2xl font-black uppercase text-xs shadow-lg shadow-orange-500/30 transition-all active:scale-95 flex items-center justify-center gap-2 mt-4"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <> {t.auth.enter} <ArrowRight size={16} /></>}
              </button>

              <div className="flex justify-center mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setForgotEmail(email);
                    setForgotError('');
                    setShowForgotModal(true);
                  }}
                  className="text-[10px] font-black uppercase text-slate-400 hover:text-[#FF6B00] transition-colors"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            </form>

            {(loginMode === 'CLASSIC' || loginMode === 'NEW_ENV') && (
              <div className="mt-8 pt-6 border-t border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center mb-4">O ingresa con</p>
                <div className="grid grid-cols-1 gap-4">
                  <button
                    type="button"
                    className="flex items-center justify-center gap-2 bg-white border-2 border-slate-200 hover:border-slate-300 py-3 rounded-2xl transition-colors font-bold text-xs text-slate-700"
                    onClick={handleGoogleAuth}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Google
                  </button>
                </div>
              </div>
            )}

            {!hideRegister && (
              <div className="mt-6 pt-6 border-t border-slate-50 text-center space-y-4">

                <div>
                  <p className="text-xs font-bold text-slate-400">
                      {loginMode === 'VET' ? '¿Quieres que tu veterinaria sea parte de la red?' :
                        loginMode === 'BREEDER' ? '¿Deseas registrar tu criadero?' :
                          loginMode === 'SHELTER' ? '¿Deseas registrar tu albergue?' : t.auth.noAccount}
                    </p>
                    <button
                      onClick={() => {
                        const roleMap: any = { 'VET': 'vet', 'BREEDER': 'breeder', 'SHELTER': 'shelter' };
                        const role = roleMap[loginMode] || 'user';
                        const url = new URL(window.location.href);
                        url.searchParams.set('role', role);
                        window.history.pushState({}, '', url);
                        onNavigate(View.REGISTER);
                      }}
                      className="text-[#FF6B00] font-black uppercase text-xs hover:underline mt-1"
                    >
                      {loginMode === 'VET' ? 'Solicitar mi cuenta Pro' :
                        loginMode === 'BREEDER' || loginMode === 'SHELTER' ? 'Enviar Solicitud' : t.auth.registerLink}
                    </button>
                  </div>
              </div>
            )}
          </>
        )}




        {/* MODAL RECUPERAR CONTRASEÑA (PASO 1) */}
        {
          showForgotModal && (
            <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-300">
              <div className="bg-white rounded-[2.5rem] p-8 max-w-sm w-full shadow-2xl border-4 border-[#1E3A8A]/20 animate-in zoom-in-95 duration-300 relative">
                <button
                  onClick={() => setShowForgotModal(false)}
                  className="absolute top-6 right-6 text-slate-300 hover:text-rose-500 transition-colors"
                >
                  <X size={20} />
                </button>
                <div className="w-16 h-16 bg-[#1E3A8A]/10 rounded-2xl flex items-center justify-center mb-6">
                  <Lock size={28} className="text-[#1E3A8A]" />
                </div>
                <h3 className="2xl font-black text-slate-800 uppercase italic tracking-tighter mb-2">Recuperar Clave</h3>
                <p className="text-slate-500 font-bold text-xs mb-6 leading-relaxed">
                  Ingresa tu correo electrónico y te enviaremos un enlace para que elijas una nueva contraseña.
                </p>
                {forgotError && (
                  <div className="bg-rose-50 text-rose-500 p-3 rounded-xl text-[10px] font-bold mb-4 flex items-center gap-2">
                    <AlertTriangle size={14} /> {forgotError}
                  </div>
                )}
                <div className="space-y-4">
                  <InputGroup icon={Mail} label="Tu Correo" placeholder="email@ejemplo.com" value={forgotEmail} onChange={(e: any) => setForgotEmail(e.target.value)} />
                  <button
                    disabled={forgotLoading}
                    onClick={async () => {
                      if (!forgotEmail) {
                        setForgotError("Ingresa tu correo");
                        return;
                      }
                      setForgotLoading(true);
                      setForgotError('');
                      try {
                        const res = await api.forgotPassword(forgotEmail);
                        if (res.success) {
                          setShowForgotModal(false);
                          setShowSentModal(true);
                        } else {
                          setForgotError(res.message || "Error al procesar");
                        }
                      } catch (e) {
                        setForgotError("Error de conexión");
                      } finally {
                        setForgotLoading(false);
                      }
                    }}
                    className="w-full bg-[#1E3A8A] text-white py-4 rounded-2xl font-black uppercase text-xs tracking-widest shadow-lg shadow-blue-100 hover:bg-blue-800 transition-all flex items-center justify-center gap-2"
                  >
                    {forgotLoading ? <Loader2 size={16} className="animate-spin" /> : <>Enviar Enlace <ArrowRight size={16} /></>}
                  </button>
                </div>
              </div>
            </div>
          )
        }

        {showSentModal && (
          <div className="fixed inset-0 z-[101] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] p-10 max-w-sm w-full text-center shadow-2xl border-4 border-[#FF6B00]/20 animate-in zoom-in-95 duration-300">
              <div className="w-20 h-20 bg-[#FF6B00] rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-emerald-100">
                <CheckCircle size={40} className="text-white animate-bounce" />
              </div>
              <h3 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter mb-2">¡Correo Enviado!</h3>
              <p className="text-slate-500 font-bold text-xs mb-8 leading-relaxed">
                Hemos enviado las instrucciones a <span className="text-slate-800">{forgotEmail}</span>. Revisa tu bandeja de entrada o SPAM.
              </p>
              <button
                onClick={() => setShowSentModal(false)}
                className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-emerald-600 transition-all"
              >
                Entendido
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const RegisterView: React.FC<AuthViewProps> = ({ onNavigate, role: forcedRole }) => {
  const { login } = useAuth();
  const { t } = useLanguage();

  // Detect requested role from prop, URL or default to 'user'
  const urlParams = new URLSearchParams(window.location.search);
  const requestedRole = forcedRole || (urlParams.get('role') as 'user' | 'vet' | 'breeder' | 'shelter') || 'user';

  const [role] = useState<'user' | 'vet' | 'breeder' | 'shelter'>(requestedRole);
  const [clinicName, setClinicName] = useState(''); // Also used for Breeder/Shelter name
  // Red social del criadero (Instagram, Facebook o TikTok): el equipo la revisa para verificar que el criadero existe.
  const [socialUrl, setSocialUrl] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);
  const [countdown, setCountdown] = useState(5);

  const [name, setName] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); // Estado ojo pass
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // Nuevo estado ojo confirm
  const [phone, setPhone] = useState(''); // Solo el número
  const [phonePrefix, setPhonePrefix] = useState('+56'); // Prefijo por defecto centralizado
  const [rut, setRut] = useState('');
  const [address, setAddress] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  const [referralCode, setReferralCode] = useState('');

  const [country, setCountry] = useState('Chile');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');

  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Efecto: Cuando cambia el País
  useEffect(() => {
    if (LOCATIONS[country]) {
      const regions = Object.keys(LOCATIONS[country].regions);
      setAvailableRegions(regions);
      setRegion('');
      setCity('');
      setAvailableCities([]);

      // Actualizar prefijo telefónico
      if (LOCATIONS[country].prefix) {
        setPhonePrefix(LOCATIONS[country].prefix.trim());
      }
    }
  }, [country]);

  // Efecto: Cuando cambia la Región
  useEffect(() => {
    if (country && region && LOCATIONS[country]?.regions[region]) {
      const cities = LOCATIONS[country].regions[region];
      setAvailableCities(cities);
      setCity('');
    }
  }, [country, region]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (role === 'user') {
      if (!name || !email || !password || !phone || !region || !city) {
        setError("Por favor completa todos los campos obligatorios");
        return;
      }
    } else {
      if (!name || !email || !password || !phone || !rut || !region || !city) {
        setError("Por favor completa todos los campos obligatorios");
        return;
      }
    }

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    if (role !== 'user' && !clinicName) {
      setError("El nombre de la entidad es obligatorio");
      return;
    }

    // Criaderos: enlace a su Instagram, Facebook o TikTok (para verificarlos antes de aprobar).
    const socialOk = (v: string) => /^(https?:\/\/)?([a-z0-9-]+\.)*(instagram\.com|facebook\.com|fb\.com|fb\.me|tiktok\.com)\/\S+/i.test(v.trim());
    if (role === 'breeder' && !socialOk(socialUrl)) {
      setError("Agrega el enlace de la página de tu criadero en Instagram, Facebook o TikTok (ej: https://instagram.com/tu_criadero)");
      return;
    }
    if (role !== 'breeder' && socialUrl.trim() && !socialOk(socialUrl)) {
      setError("El enlace de red social debe ser de Instagram, Facebook o TikTok");
      return;
    }

    setLoading(true);

    const finalAddress = addressDetail ? `${address} (${addressDetail})` : address;
    const finalPhone = `${phonePrefix.trim()}${phone.trim()}`;
    const fullName = name.trim();

    try {
      if (role === 'user') {
        const finalData = {
          name: fullName, email, password, phone: finalPhone,
          region, city, address: finalAddress,
          referralCode,
          country
        };
        const res = await api.register(finalData);
        if (res && res.message === 'EXITO' && res.user) {
          setSuccessData(res);
          setShowSuccessModal(true);
          login(res.user, res.token);
        } else {
          setError(typeof res.error === 'string' ? res.error : 'Error al registrar');
        }
      } else {
        // Partner Request
        const partnerData = {
          type: role,
          doctor_name: name,
          clinic_name: clinicName,
          email,
          phone: finalPhone,
          address: finalAddress,
          city,
          region,
          country,
          business_id_number: rut,
          social_url: socialUrl.trim(),
          comments: referralCode ? `Referido por: ${referralCode}` : 'Registro web',
          plan: 'ESSENTIALS' // Default plan
        };

        const res = await api.createPartnerRequest(partnerData);
        if (res.success) {
          setShowSuccessModal(true);
          setSuccessData({ isPartner: true });
        } else {
          setError(res.error || 'Error al enviar solicitud');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setError('');
    const res = await signInWithGoogle();
    if (res.success && res.user) {
      try {
        const apiRes = await api.socialLogin(res.user.email || '', res.user.name || 'Usuario', 'google');
        if (apiRes && apiRes.message === 'EXITO' && apiRes.user) {
          setSuccessData(apiRes);
          setShowSuccessModal(true);
          login(apiRes.user, apiRes.token);
        } else {
          setError(apiRes.error || 'Error al registrar con Google');
        }
      } catch (e) {
        setError(t.auth.connectionError);
      }
    } else {
      setError(res.error || 'Autenticación con Google cancelada o fallida');
    }
    setLoading(false);
  };

  // Efecto para cuenta regresiva y redirección automática
  useEffect(() => {
    let timer: any;
    if (showSuccessModal && successData) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleRedirect(successData);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [showSuccessModal, successData]);

  const handleRedirect = (data: any) => {
    if (data?.isPartner) {
      onNavigate(View.LANDING);
      return;
    }
    if (!data || !data.user) return;
    login(data.user, data.token);
    if (localStorage.getItem('checkout_pending')) {
      localStorage.removeItem('checkout_pending');
      onNavigate(View.CHECKOUT);
      return;
    }
    // Llegó desde "Dar en adopción" en /adoptame sin cuenta: lo mandamos al Hub
    // (no a Mis Mascotas) dejando la bandera puesta — el Hub la detecta al
    // montar y abre ahí mismo el modal paso a paso para crear y publicar su
    // mascota, sin desviarlo a una ruta aparte.
    if (localStorage.getItem('adoption_give_pending')) {
      onNavigate(View.USER_HUB);
      return;
    }
    onNavigate(data.user.role?.includes('root') ? View.ROOT_PANEL : View.PET_PROFILES);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[85vh] px-4 animate-in fade-in slide-in-from-bottom-4 py-10 pb-32 pt-[env(safe-area-inset-top)]">
      <div className={`
        bg-white p-8 rounded-[3rem] shadow-xl border-4 w-full max-w-3xl relative overflow-hidden
        ${role === 'vet' ? 'border-[#1E3A8A]/20' : role === 'breeder' ? 'border-emerald-500/20' : role === 'shelter' ? 'border-orange-500/20' : 'border-[#FF6B00]/20'}
      `}>
        {/* Barra superior dinámica */}
        <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${role === 'vet' ? 'from-[#1E3A8A] to-blue-400' :
          role === 'breeder' ? 'from-emerald-400 to-teal-500' :
            role === 'shelter' ? 'from-orange-400 to-amber-500' :
              'from-[#FF6B00] to-orange-400'
          }`}></div>

        <div className="text-center mb-8">
          <h2 className="text-3xl font-black text-slate-800 italic uppercase tracking-tighter">
            {role === 'user' ? t.auth.createAccount :
              role === 'vet' ? 'Registro Veterinario' :
                role === 'breeder' ? 'Registro Criadero' : 'Registro Albergue'}
          </h2>
          <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-2">
            {role === 'user' ? t.auth.join : 'Solicita tu incorporación al ecosistema'}
          </p>
        </div>


        {/* Notificación Flotante (Estilo Premium) */}
        {(error || (loading && !error)) && (
          <div className={`
            fixed top-4 left-1/2 -translate-x-1/2 z-50 
            px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 border-2
            animate-in slide-in-from-top-4 fade-in duration-300
            ${error ? 'bg-rose-50 border-rose-100 text-rose-600' : 'bg-white border-[#FF6B00]/20 text-[#FF6B00]'}
          `}>
            {error ? <AlertTriangle size={24} className="shrink-0" /> : <Loader2 size={24} className="animate-spin shrink-0" />}
            <div>
              <h4 className="font-black text-sm uppercase tracking-wider">{error ? 'Atención' : 'Procesando'}</h4>
              <p className="text-xs font-bold opacity-80">{error || 'Creando tu cuenta, por favor espera...'}</p>
            </div>
            {error && (
              <button onClick={() => setError('')} className="ml-4 p-1 hover:bg-rose-100 rounded-full transition-colors">
                <X size={16} />
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* SECCIÓN 1: DATOS DE ACCESO */}
          <div className="bg-slate-50 p-5 rounded-[2rem] border border-slate-100">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <User size={12} /> Credenciales
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {role !== 'user' && (
                <div className="md:col-span-2">
                  <InputGroup
                    icon={Building2}
                    label={role === 'vet' ? 'Nombre de la Clínica' : role === 'breeder' ? 'Nombre del Criadero' : 'Nombre del Albergue'}
                    placeholder="Ej: Centro Mascotas Nexus"
                    value={clinicName}
                    onChange={(e: any) => setClinicName(e.target.value)}
                  />
                </div>
              )}
              {role !== 'user' && (
                <div className="md:col-span-2">
                  <InputGroup
                    icon={Globe}
                    label={role === 'breeder' ? 'Instagram, Facebook o TikTok de tu criadero' : 'Instagram, Facebook o TikTok (opcional)'}
                    placeholder="https://instagram.com/tu_criadero"
                    value={socialUrl}
                    onChange={(e: any) => setSocialUrl(e.target.value)}
                    required={role === 'breeder'}
                  />
                  {role === 'breeder' && <p className="text-[10px] font-medium text-slate-400 mt-1.5 ml-2 leading-relaxed">Lo usamos para verificar que tu criadero existe antes de aprobar tu cuenta. No necesitas subir documentos.</p>}
                </div>
              )}
              {role === 'user' ? (
                <div className="md:col-span-2">
                  <InputGroup icon={User} label="Nombre" placeholder="Ej: Juan Antonio" value={name} onChange={(e: any) => setName(e.target.value)} />
                </div>
              ) : (
                <>
                  <InputGroup icon={User} label="Nombre del Responsable" placeholder="Ej: Juan Pérez" value={name} onChange={(e: any) => setName(e.target.value)} />
                  <InputGroup icon={User} label={LOCATIONS[country]?.idName || t.auth.rut} placeholder={LOCATIONS[country]?.idPlaceholder || "ID Number"} value={rut} onChange={(e: any) => setRut(formatGenericID(e.target.value, country))} />
                </>
              )}
              <InputGroup icon={Mail} label={t.auth.email} type="email" placeholder="tu@email.com" value={email} onChange={(e: any) => setEmail(e.target.value)} />
              <div className="space-y-1 w-full relative">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">{t.auth.phone}</label>
                <div className="flex items-center gap-3 bg-white border-2 border-slate-200 rounded-2xl px-4 py-3 focus-within:border-[#FF6B00] transition-all">
                  <Phone size={16} className="text-slate-400 shrink-0" />
                  <div className="flex items-center gap-2 w-full">
                    <select
                      className="text-slate-500 font-bold text-xs bg-slate-100 px-2 py-1 rounded-lg outline-none cursor-pointer appearance-none text-center"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                    >
                      {Object.entries(LOCATIONS).map(([c, data]) => (
                        <option key={c} value={c}>{data.prefix}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      required
                      className="bg-transparent outline-none flex-1 font-bold text-slate-700 text-xs w-full placeholder:text-slate-300"
                      placeholder="12345678"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                </div>
              </div>

              <InputGroup
                icon={Lock}
                label={t.auth.password}
                type={showPassword ? "text" : "password"}
                placeholder="••••••"
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                isPassword={true}
                onTogglePassword={() => setShowPassword(!showPassword)}
                showPassword={showPassword}
              />
              <InputGroup
                icon={Lock}
                label="Confirmar Contraseña"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="••••••"
                value={confirmPassword}
                onChange={(e: any) => setConfirmPassword(e.target.value)}
                isPassword={true}
                onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
                showPassword={showConfirmPassword}
              />


            </div>
          </div>

          {/* SECCIÓN 2: UBICACIÓN */}
          <div className="bg-emerald-50/50 p-5 rounded-[2rem] border border-[#FF6B00]/20">
            <h4 className="text-[10px] font-black text-[#FF6B00] uppercase tracking-widest mb-1 flex items-center gap-2">
              <MapPin size={12} /> {t.auth.addressTitle || 'Ubicación'}
            </h4>
            {role === 'user' && (
              <p className="text-[10px] text-slate-500 font-bold mb-4">
                Esta información es confidencial y se usará exclusivamente para facilitar el rescate de tu mascota en caso de perderse.
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Selector País */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">{t.auth.country}</label>
                <div className="flex items-center gap-3 bg-white border-2 border-slate-200 rounded-2xl px-4 py-3 focus-within:border-[#FF6B00] transition-all">
                  <Globe size={16} className="text-slate-400 shrink-0" />
                  <CustomSelect
                    value={country}
                    onChange={setCountry}
                    options={Object.keys(LOCATIONS)}
                    placeholder="Selecciona País"
                  />
                </div>
              </div>

              {/* Selector Región */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">{t.auth.region}</label>
                <div className="flex items-center gap-3 bg-white border-2 border-slate-200 rounded-2xl px-4 py-3 focus-within:border-[#FF6B00] transition-all">
                  <Map size={16} className="text-slate-400 shrink-0" />
                  <CustomSelect
                    value={region}
                    onChange={setRegion}
                    options={availableRegions}
                    placeholder="Selecciona Región"
                    disabled={!country}
                  />
                </div>
              </div>

              {/* Selector Comuna */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Comuna / Ciudad</label>
                <div className="flex items-center gap-3 bg-white border-2 border-slate-200 rounded-2xl px-4 py-3 focus-within:border-[#FF6B00] transition-all">
                  <MapPin size={16} className="text-slate-400 shrink-0" />
                  <CustomSelect
                    value={city}
                    onChange={setCity}
                    options={availableCities}
                    placeholder="Selecciona Ciudad"
                    disabled={!region}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Dirección Exacta */}
              <InputGroup icon={MapPin} label={t.auth.address} placeholder="123 Main St" value={address} onChange={(e: any) => setAddress(e.target.value)} />

              {/* Detalle Extra */}
              <InputGroup icon={Home} label={t.auth.addressDetail} placeholder="Apt 4B" value={addressDetail} onChange={(e: any) => setAddressDetail(e.target.value)} required={false} />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`
              w-full py-4 rounded-2xl font-black uppercase text-xs shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 text-white
              ${role === 'vet' ? 'bg-[#3F51B5] hover:bg-indigo-600 shadow-indigo-500/30' :
                role === 'breeder' ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/30' :
                  'bg-[#FF6B00] hover:bg-orange-600 shadow-orange-500/30'}
            `}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : (
              role === 'user' ? <>{t.auth.registerBtn} <CheckCircle size={16} /></> : <>Enviar Solicitud <Send size={16} /></>
            )}
          </button>
        </form>

        {role === 'user' && (
          <div className="mt-8 pt-6 border-t border-slate-100">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center mb-4">O regístrate con</p>
            <div className="grid grid-cols-1 gap-4">
              <button
                type="button"
                className="flex items-center justify-center gap-2 bg-white border-2 border-slate-200 hover:border-slate-300 py-3 rounded-2xl transition-colors font-bold text-xs text-slate-700"
                onClick={handleGoogleAuth}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Google
              </button>
            </div>
          </div>
        )}

        {role === 'user' && (
          <div className="mt-8 text-center">
            <p className="text-xs font-bold text-slate-400">{t.auth.haveAccount}</p>
            <button onClick={() => onNavigate(View.LOGIN)} className="text-[#FF6B00] font-black uppercase text-xs hover:underline mt-1">
              {t.auth.loginLink}
            </button>

          </div>
        )}
      </div>

      {showSuccessModal && (
        <div className="fixed inset-0 z-[102] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-300">
          <div className="bg-white rounded-[3rem] p-10 max-w-sm w-full text-center shadow-2xl border-4 border-[#FF6B00]/20 animate-in zoom-in-95 duration-300 relative">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl ${role === 'vet' ? 'bg-[#3F51B5] shadow-indigo-100' :
              role === 'breeder' ? 'bg-emerald-500 shadow-emerald-100' :
                'bg-[#FF6B00] shadow-orange-100'
              }`}>
              <CheckCircle size={48} className="text-white animate-bounce" />
            </div>
            <h3 className="text-3xl font-black text-slate-800 uppercase italic tracking-tighter mb-2">
              {successData?.isPartner ? '¡Solicitud Enviada!' : '¡Bienvenido!'}
            </h3>
            <p className="text-slate-500 font-bold text-sm mb-4 leading-relaxed">
              {successData?.isPartner
                ? `Tu solicitud fue recibida. Te enviamos un correo de confirmación a ${email} (revisa también spam). Nuestro equipo revisará tus datos y te responderá por ese mismo correo, normalmente en 24 a 48 horas hábiles.`
                : 'Tu cuenta ha sido creada con éxito. Prepárate para darle a tu mascota la mejor experiencia en Amigo Peludo.'}
            </p>

            <div className="bg-slate-50 rounded-2xl p-4 mb-6">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                {successData?.isPartner ? 'Volviendo al inicio en...' : 'Iniciando sesión en...'}
              </p>
              <div className={`text-2xl font-black mt-1 ${role === 'vet' ? 'text-[#3F51B5]' :
                role === 'breeder' ? 'text-emerald-500' :
                  'text-[#FF6B00]'
                }`}>{countdown} <span className="text-xs">seg</span></div>
            </div>

            <button
              onClick={() => handleRedirect(successData)}
              className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl shadow-slate-900/20 hover:bg-emerald-600 transition-all active:scale-95 flex items-center justify-center gap-3"
            >
              {successData?.isPartner ? 'Entendido' : 'Comenzar Aventura'} <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const ResetPasswordView: React.FC<AuthViewProps> = ({ onNavigate }) => {
  const { t } = useLanguage();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const token = new URLSearchParams(window.location.search).get('token') || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    setError('');

    try {
      const res = await api.resetPassword(token, password);
      if (res.success) {
        setSuccess(true);
        setTimeout(() => { window.location.href = '/login'; }, 3000);
      } else {
        setError(res.message || "Error al restablecer contraseña");
      }
    } catch (err) {
      setError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[85vh] px-4 py-32">
        <div className="bg-white p-10 rounded-[3rem] shadow-xl text-center max-w-sm border-4 border-[#FF6B00]/20">
          <div className="w-20 h-20 bg-[#FF6B00] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-white" />
          </div>
          <h2 className="text-2xl font-black text-slate-800 uppercase italic mb-2 tracking-tighter">¡Éxito!</h2>
          <p className="text-slate-500 font-bold text-sm">Tu contraseña ha sido actualizada. Serás redirigido al login en unos segundos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[85vh] px-4 py-32">
      <div className="bg-white p-8 rounded-[3rem] shadow-xl border-4 border-amber-50 w-full max-w-md relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 to-orange-500"></div>

        <div className="text-center mb-8">
          <h2 className="text-3xl font-black text-slate-800 italic uppercase tracking-tighter">Nueva Contraseña</h2>
          <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-2">Elige tu nueva clave de acceso</p>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-100 p-4 rounded-2xl mb-6 flex items-center gap-3 text-rose-500 text-xs font-bold">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <InputGroup
            icon={Lock}
            label="Nueva Contraseña"
            type={showPassword ? "text" : "password"}
            placeholder="••••••"
            value={password}
            onChange={(e: any) => setPassword(e.target.value)}
            isPassword={true}
            onTogglePassword={() => setShowPassword(!showPassword)}
            showPassword={showPassword}
          />
          <InputGroup
            icon={ShieldCheck}
            label="Confirmar Nueva Contraseña"
            type={showPassword ? "text" : "password"}
            placeholder="••••••"
            value={confirmPassword}
            onChange={(e: any) => setConfirmPassword(e.target.value)}
          />

          <button
            type="submit"
            disabled={loading || !token}
            className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black uppercase text-xs shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 mt-4"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Actualizar Contraseña"}
          </button>

          {!token && (
            <p className="text-rose-500 text-[10px] font-bold text-center mt-2 uppercase tracking-wider">Token faltante o inválido</p>
          )}
        </form>
      </div>
    </div>
  );
};

// Componente Helper para Inputs mejorado
const InputGroup = ({ icon: Icon, label, type = "text", placeholder, value, onChange, required = true, isPassword = false, onTogglePassword, showPassword }: any) => (
  <div className="space-y-1 w-full relative">
    <label className="text-[10px] font-black text-slate-400 uppercase ml-2">{label}</label>
    <div className="flex items-center gap-3 bg-white border-2 border-slate-200 rounded-2xl px-4 py-3 focus-within:border-[#FF6B00] transition-all">
      <Icon size={16} className="text-slate-400 shrink-0" />
      <input
        type={type}
        required={required}
        className="bg-transparent outline-none flex-1 font-bold text-slate-700 text-xs w-full placeholder:text-slate-300"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
      {isPassword && onTogglePassword && (
        <button
          type="button"
          onClick={onTogglePassword}
          className="text-slate-400 hover:text-[#FF6B00] focus:outline-none p-1"
        >
          {showPassword ? <Eye size={16} className="text-emerald-500" /> : <Eye size={16} />}
        </button>
      )}
    </div>
  </div>
);